import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { Flag, Search, X } from 'lucide-react'
import {
  fetchStandingAlerts,
  flagStanding,
  sendStandingAlert,
  sendStandingAlerts,
  type StandingAlert,
} from '../api/attendance'
import { useCan } from '../useSession'
import ConfirmAction from './ConfirmAction'

/**
 * Where every competitive dancer stands, worst first (D-165, D-590, D-765).
 *
 * **The screen the "tell the family" queue never had.** It sits first under
 * Attendance because it is the thing worth seeing the moment the screen
 * opens; Absences is second and Missing last, since an unmarked register is
 * something Tonya chases a teacher about rather than reads every day.
 *
 * **Everybody has a row, not only the ones in trouble** (D-765). It was an
 * alert queue, so *is she all right?* about a named dancer could not be asked
 * here at all - the only way to find out about one who was fine was to open her
 * record. The worst-first order is unchanged; what the list gained is a search,
 * two quick filters and a row for everybody else.
 *
 * **Red rows can be sent, amber rows never.** Amber is a class under the line
 * with the overall fine (D-588), and the only email says "below the
 * threshold", which is false of her. The server decides `sendable`; this
 * screen only shows it.
 *
 * **A teacher sees her own dancers and flags rather than sends.** The flag is
 * a note to whoever runs attendance and a mark on the row - not an ask,
 * because nobody answers it (A-17). Whether the person looking may send at
 * all is `SendAnnouncements`, which no teacher holds.
 */
export default function StandingView() {
  const queryClient = useQueryClient()
  const maySend = useCan('SendAnnouncements')

  // The same key the tab count reads (`useStandingCount`), so opening the
  // view is one request rather than two carrying every family's names.
  const { data, isPending, isError } = useQuery({
    queryKey: ['standing-alerts', 'nav'],
    queryFn: fetchStandingAlerts,
  })

  const [confirming, setConfirming] = useState(false)

  const send = useMutation({
    mutationFn: sendStandingAlerts,
    onSuccess: (report) => {
      queryClient.setQueryData(['standing-alerts', 'nav'], report)
      setConfirming(false)
    },
  })

  /*
   * <b>The company team only</b> (D-763, narrowed by D-768). `asked`: *"there
   * need to be a filter on this page to show the company kids."*
   *
   * They are judged against their own, higher figure (D-652), so "who on the
   * team is slipping" is a different question from "who in the studio is" - and
   * the second buries the first the moment there are a dozen rows.
   *
   * <b>A tick box, not three choices.</b> D-763 shipped Everyone / Company /
   * Everyone else and argued the third was the one a tick box would lose;
   * `asked`, on seeing it: *"lets get rid of the everyone else button entirely,
   * its not needed."* Nobody asks for the studio minus the team, and the option
   * cost a control shape that made the other filter unreadable.
   *
   * Held on the screen rather than in the URL: it is a way of reading this
   * list, not a place.
   */
  const [companyOnly, setCompanyOnly] = useState(false)

  /*
   * <b>Below the line only</b> (D-765). `asked`: *"only those that are below
   * the line (would be under 80% for normal comp or under the 100% for company
   * etc)."*
   *
   * Her own line, never a number written here: the state is computed against
   * the threshold that dancer is held to (D-652), so this filter stays right
   * the day the company figure moves.
   */
  const [under, setUnder] = useState(false)

  /*
   * <b>By name</b> (D-765). The list is every competitive dancer now, so the
   * question *how is this one doing?* needs an answer that is not scrolling.
   * The dancer's name only - matching the household name too would quietly
   * return siblings nobody asked about.
   */
  const [search, setSearch] = useState('')

  const everyone = data?.alerts ?? []

  /*
   * <b>Offered only when it would do something</b> (D-204). Nobody marked
   * company means the control could only ever empty the screen - and until
   * Tonya has marked anybody, that is every studio.
   */
  const anyCompany = everyone.some((a) => a.isCompany)

  const needle = search.trim().toLowerCase()

  const alerts = everyone.filter(
    (alert) =>
      // Folded in rather than trusted on its own: the tick box renders only
      // while somebody is on the team, so a refetch that loses them would
      // otherwise leave a filter on with no control to clear it.
      (!companyOnly || !anyCompany || !!alert.isCompany) &&
      (!under || alert.state === 'Under') &&
      (needle === '' || alert.dancerName.toLowerCase().includes(needle)),
  )

  const filtered = companyOnly || under || needle !== ''

  /*
   * <b>How many families the send would reach that are not on screen</b>
   * (D-763, D-765).
   *
   * The bulk send walks every sendable row on the server and cannot be
   * narrowed from here, so offering it under a filter that hides some of them
   * emails families the reader cannot see - and an email cannot be taken back.
   * This asks that question directly rather than through a proxy, which is
   * what lets <i>Below the line</i> keep the button: that filter hides nobody
   * who would be emailed, and it is the view somebody about to send is most
   * likely to be in.
   */
  const shown = new Set(alerts.map((a) => a.dancerId))
  const hiddenSendable = everyone.filter((a) => a.sendable && !shown.has(a.dancerId)).length

  // About the send, so counted over the studio's list rather than the reader's.
  const amber = everyone.filter((a) => a.state === 'ClassUnder').length

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Attendance</p>
        <h1 className="font-display text-2xl">Standing</h1>
        {/*
          **"the line they are held to", not "the 80% line"** (D-652). The
          studio figure was written into this sentence when it was true of
          everybody; the company team is judged against its own, so naming one
          number here tells Tonya something false about half the rows
          underneath it — and she reads this immediately before pressing send.

          **And not "she"** (D-769). `asked`: *"its not just female dancers."*
          The studio is mostly girls and the copy had quietly made that a rule;
          a boy's family reading his own record should not find the app
          describing somebody else.
        */}
        <p className="mt-1.5 max-w-[42rem] text-[13.5px] text-ink-3">
          Every competitive dancer, worst first. The line a dancer is held to is{' '}
          {data?.threshold ?? 80}% for most and higher for the company team. A family is told only
          when the whole figure is under and every class that counts has been marked.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2.5">
          <div className="flex min-w-[13rem] flex-1 items-center gap-2 rounded-lg border border-line-2 bg-surface px-3 py-2 focus-within:border-accent">
            <Search size={16} strokeWidth={2} className="shrink-0 text-ink-3" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by dancer"
              aria-label="Search by dancer"
              className="min-w-0 flex-1 bg-transparent text-[13.5px] outline-none"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear the search"
                className="shrink-0 text-ink-3 hover:text-ink"
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            )}
          </div>

          {/*
            <b>Two tick boxes, and nothing else</b> (D-768, amending D-763).

            `asked`, twice on the QA review: *"its not clear that you can toggle
            two at the same time"*, then *"lets get rid of the everyone else
            button entirely, its not needed... lets just do a way to quickly
            search for company dancers, and then below the line people."*

            It was a pick-one group of three sitting beside an independent
            toggle, all four the same shape - so the toggle read as a fourth
            alternative, and the third option existed for a question nobody
            asks. Two tick boxes say what they do: either can be on, both can be
            on, and off means everybody. Real checkboxes rather than buttons
            wearing `aria-pressed`, because the tick is the affordance and the
            label is the hit area, which gets the tap target for free.
          */}
          {anyCompany && (
            <Toggle checked={companyOnly} onChange={setCompanyOnly} label="Company dancers" />
          )}

          <Toggle checked={under} onChange={setUnder} label="Below the line" />
        </div>

        {/*
          The count of what is hidden, so a short list never reads as a quiet
          studio (D-209). Filtering is a choice somebody made; a screen that
          forgets it is one they argue with.
        */}
        {filtered && alerts.length > 0 && (
          <p className="mt-2 text-[12.5px] text-ink-3">
            Showing {alerts.length} of {everyone.length}.
          </p>
        )}
      </header>

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}
      {/* A failed lookup is never a statement about the data (D-209). */}
      {isError && (
        <p role="alert" className="text-sm text-crit">
          Could not load standing just now. Please try again.
        </p>
      )}

      {data && alerts.length === 0 && (
        <p className="rounded-2xl border border-line bg-surface px-4 py-4 text-[13.5px] text-ink-2">
          {/*
            <b>Which of three different facts this is</b> (D-209, D-763). An
            empty list under a filter says something about the filter and
            nothing about the studio - and on the screen that decides who is
            chased, *nobody is under the line* is the wrong sentence to get
            wrong.
          */}
          {everyone.length === 0
            ? 'No competitive dancers to show.'
            : needle !== ''
              ? 'Nobody by that name. Clear the search to see the rest.'
              : 'Nobody here, with these filters on.'}
        </p>
      )}

      {data && alerts.length > 0 && (
        <>
          {/*
            <b>Telling the families is the studio's whole list, so it is not
            offered while somebody it would reach is off screen</b> (D-763,
            found by the audit; narrowed by D-765).

            Left enabled under a narrowing filter, pressing it emails every
            family under the line - most of them not on screen - and an email
            cannot be taken back. Plain words rather than a second control: the
            filters are directly above this, and a fourth way to press one of
            them is two implementations of one gesture on one screen.
          */}
          {maySend && hiddenSendable > 0 && (
            <p className="mb-4 rounded-xl border border-line bg-surface-2 px-4 py-3 text-[13px] text-ink-2">
              Telling families would also reach {hiddenSendable}{' '}
              {hiddenSendable === 1 ? 'family' : 'families'} not shown here. Clear the filters to
              send to everyone, or tell one family from her own row.
            </p>
          )}

          {maySend && hiddenSendable === 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-line bg-surface px-4 py-3 text-[13px]">
              <span className="min-w-0 flex-1 text-ink-2">
                {data.sendable === 0
                  ? 'Nothing can be sent right now.'
                  : `${data.sendable} ${data.sendable === 1 ? 'family' : 'families'} can be told.`}
                {data.blocked > 0 && (
                  <> {data.blocked} {data.blocked === 1 ? 'is' : 'are'} waiting on an unmarked class.</>
                )}
                {amber > 0 && (
                  <> {amber} {amber === 1 ? 'is' : 'are'} above the line overall and will not be emailed.</>
                )}
              </span>
              <button
                type="button"
                disabled={data.sendable === 0 || send.isPending}
                onClick={() => setConfirming(true)}
                className="rounded-lg bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-50"
              >
                Tell {data.sendable === 1 ? 'the family' : 'the families'}
              </button>
              {data.sent && (
                <span role="status" className="text-ink-3">
                  Sent to {data.delivered ?? 0}
                  {(data.failed ?? 0) > 0 && <>, {data.failed} could not be delivered</>}.
                </span>
              )}
            </div>
          )}

          {confirming && (
            <ConfirmAction
              title={`Tell ${data.sendable} ${data.sendable === 1 ? 'family' : 'families'}?`}
              body="Each family under the line gets the attendance email. It cannot be taken back once it has gone."
              confirmLabel="Yes, tell them"
              busyLabel="Sending..."
              busy={send.isPending}
              onConfirm={() => send.mutate()}
              onDismiss={() => setConfirming(false)}
            />
          )}

          <ul className="flex flex-col gap-2">
            {alerts.map((alert) => (
              <Row key={alert.dancerId} alert={alert} maySend={maySend} />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

/**
 * One quick filter: on, or off (D-768).
 *
 * A real checkbox, so the tick says *independent toggle* and two of them side
 * by side read as two things that combine. The label is the control, which is
 * what keeps the tap target over 24px without a size written anywhere (D-255).
 */
function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (on: boolean) => void
  label: string
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-semibold ${
        checked ? 'border-accent text-ink' : 'border-line-2 text-ink-2 hover:border-ink-3'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-3.5 accent-accent"
      />
      {label}
    </label>
  )
}

/** A flag's instant as the day it happened where the viewer is, not in UTC (D-68). */
export function flaggedOn(at: string): string {
  return new Date(at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

const TONE = {
  Under: { dot: 'bg-crit', text: 'text-crit' },
  ClassUnder: { dot: 'bg-warn', text: 'text-warn' },
  Good: { dot: 'bg-good', text: 'text-good' },
  NotYet: { dot: 'bg-ink-3', text: 'text-ink-3' },
} as const

function Row({ alert, maySend }: { alert: StandingAlert; maySend: boolean }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [flagging, setFlagging] = useState(false)
  const [note, setNote] = useState('')
  const [telling, setTelling] = useState(false)

  const flag = useMutation({
    mutationFn: () => flagStanding(alert.dancerId, note.trim() || null),
    onSuccess: (flagged) => {
      queryClient.setQueryData(['standing-alerts', 'nav'], (old: { alerts: StandingAlert[] } | undefined) =>
        old && { ...old, alerts: old.alerts.map((a) => (a.dancerId === alert.dancerId ? { ...a, flag: flagged } : a)) },
      )
      setFlagging(false)
      setNote('')
    },
  })

  const tell = useMutation({
    mutationFn: () => sendStandingAlert(alert.dancerId),
    onSuccess: (report) => {
      /*
       * The bulk bar's "Sent to N" is about the bulk send. Writing this
       * report's own `sent` through would light it up the moment one row was
       * told, which reads as having emailed everybody - and the row itself
       * already says what happened, because the row is what happened.
       */
      queryClient.setQueryData(['standing-alerts', 'nav'], {
        ...report,
        sent: false,
        delivered: 0,
        failed: 0,
      })
      setTelling(false)
    },
  })

  const tone = TONE[alert.state]
  const where =
    alert.state === 'Under'
      ? alert.classesUnder.length > 0
        ? `Overall, and ${alert.classesUnder.join(', ')}`
        : 'Overall'
      : alert.state === 'ClassUnder'
        ? alert.classesUnder.join(', ')
        : alert.state === 'NotYet'
          ? 'Nothing counted yet'
          : 'On track'

  /*
   * <b>Anywhere in the row opens her record</b> (D-765). `asked`: *"update it
   * to be able to click anywhere in the row to open that dancers attendance
   * reecord instead of having to click specfically on their name."*
   *
   * The name stays a real link beside it, and that is what carries the
   * keyboard, the middle click and the right-click menu - a row that answers
   * only a mouse click is a row nobody can tab to. A click that landed on a
   * control belongs to that control: flagging somebody is not a request to
   * leave the page.
   *
   * <b>And the dialog is inside the row.</b> `Modal` renders no portal, so its
   * overlay is a descendant of this `li` - which meant dismissing *"tell this
   * family?"* by tapping the dim backdrop, the ordinary way to change your
   * mind, threw the reader onto the dancer's record instead. Found by an
   * audit, on the one control here that sends an email nobody can recall.
   */
  const open = (event: React.MouseEvent<HTMLLIElement>) => {
    const target = event.target as HTMLElement
    if (target.closest('a, button, input, textarea, label, form, [data-modal-overlay]')) return
    // Selecting a name to copy it is not a click through to her record.
    if (window.getSelection()?.toString()) return
    navigate(`/admin/dancers/${alert.dancerId}`)
  }

  return (
    <li
      onClick={open}
      className="cursor-pointer rounded-2xl border border-line bg-surface px-4 py-3 hover:border-ink-3"
      data-state={alert.state}
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <Link
          to={`/admin/dancers/${alert.dancerId}`}
          // `py-1` for the height: measured 160x21 on a phone and a touch
          // target wants 24 (D-255). It is how she opens the dancer.
          className="min-w-[10rem] py-1 text-[14px] font-semibold text-ink hover:underline"
        >
          {alert.dancerName}
        </Link>

        <span className={`tabular text-[13px] font-semibold ${tone.text}`}>
          <span className={`mr-1.5 inline-block size-2 rounded-full align-middle ${tone.dot}`} aria-hidden="true" />
          {alert.percentage === null ? '—' : `${alert.percentage}%`}
        </span>

        <span className="min-w-0 flex-1 text-[12.5px] text-ink-2 wrap-anywhere">{where}</span>

        <span className="text-[12px] text-ink-3">
          {alert.recipients.length === 0
            ? 'No email on file'
            : `${alert.recipients.length} ${alert.recipients.length === 1 ? 'adult' : 'adults'} would hear`}
        </span>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
        {/* What stops the email, said in words (D-165, D-590). */}
        {alert.state === 'ClassUnder' && (
          <span className="text-ink-3">Above the line overall &mdash; no email.</span>
        )}
        {alert.state === 'Under' && alert.blocked && <span className="text-warn">{alert.blocked}</span>}

        {/*
          <b>When this family was last told, on the row that offers to tell
          them again</b> (D-766). `asked`: *"if we don't have a way to tell that
          a family has already been told we should probably add that, and
          include a way to also view when they were last told."*

          It carries the figure it went out about, because *told on 20 Sep* is
          the wrong question once she has climbed. A telling that reached
          nobody says so: that is the one case where sending again is plainly
          right, and a bare date would hide it.
        */}
        {alert.told && (
          <span className="text-ink-3">
            Told by {alert.told.byName} on {flaggedOn(alert.told.at)}
            {alert.told.percentage !== null && <> at {alert.told.percentage}%</>}
            {alert.told.recipients === 0 && <> &middot; it reached nobody</>}
          </span>
        )}

        {alert.flag && (
          <span className="text-ink-3">
            <Flag size={12} className="mr-1 inline align-[-1px]" aria-hidden="true" />
            {/* The instant, shown in the viewer's own day - never the UTC
                date sliced off the string, which is tomorrow after ~7pm on a
                class night (D-68). */}
            Flagged by {alert.flag.byName} on {flaggedOn(alert.flag.at)}
            {alert.flag.note && <> &middot; &ldquo;{alert.flag.note}&rdquo;</>}
          </span>
        )}

        {/*
          <b>One family, from her own row</b> (D-766). `asked`: *"just a button
          per row if needing to send to just a specific dancer's family instead
          of all ... more flexible that way."*

          <b>The same rule as the bulk send</b>: only a row the server calls
          sendable offers it, and the server recomputes her standing before it
          sends - so a list left open on a phone cannot email a family about a
          line the dancer has since climbed back over.
        */}
        {maySend && alert.sendable && (
          <button
            type="button"
            disabled={tell.isPending}
            onClick={() => setTelling(true)}
            className="rounded-lg border border-accent px-2.5 py-1 text-[12px] font-semibold text-accent-2 disabled:opacity-50"
          >
            {alert.told ? 'Tell them again' : 'Tell them'}
          </button>
        )}

        {tell.isError && (
          <span role="alert" className="text-crit">
            {(tell.error as Error).message}
          </span>
        )}

        {!maySend && !alert.flag && !flagging && (
          <button
            type="button"
            onClick={() => setFlagging(true)}
            className="rounded-lg border border-accent px-2.5 py-1 text-[12px] font-semibold text-accent-2"
          >
            Flag for the studio
          </button>
        )}
      </div>

      {telling && (
        <ConfirmAction
          title={`Tell ${alert.dancerName}'s family?`}
          // Always names the adults, because a row with nobody to email is
          // never sendable and so never offers this (the server's blocker says
          // so) - a line for that state would describe something that cannot
          // happen, which is the mistake D-763 wrote down.
          body={`${alert.recipients.join(' and ')} will get the attendance email. It cannot be taken back once it has gone.`}
          confirmLabel="Yes, tell them"
          busyLabel="Sending..."
          busy={tell.isPending}
          onConfirm={() => tell.mutate()}
          onDismiss={() => setTelling(false)}
        />
      )}

      {flagging && (
        <form
          className="mt-2 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            flag.mutate()
          }}
        >
          <label className="text-[12px] text-ink-2">
            Anything the studio should know? (optional)
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13px] outline-none focus:border-accent"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={flag.isPending}
              className="rounded-lg bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-on-accent disabled:opacity-50"
            >
              {flag.isPending ? 'Flagging...' : 'Flag'}
            </button>
            <button
              type="button"
              onClick={() => setFlagging(false)}
              className="rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold text-ink-2"
            >
              Cancel
            </button>
          </div>
          {flag.isError && (
            <p role="alert" className="text-[12px] text-crit">
              {(flag.error as Error).message}
            </p>
          )}
        </form>
      )}
    </li>
  )
}
